import React, { useEffect, useState, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { getConnectionInfo, getEndpoint, getDate, MatchContext, ConnectionContext, displayErrorMessage, displayLoader, getFallbackImage } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { Tab, TabsList, TabPanel, Tabs } from '@mui/base'; // https://mui.com/base-ui/react-tabs/
import { Sidebar } from "./components";
import { GameLogBox } from "./components/gameLogBox";
import { TeamLogo } from "./components/teamLogo";
import { AcaSelect } from "./components/forms/acaSelect";
import { Option } from '@mui/base/Option';


const PlayerApp = (props) => {
    const { 
        name = '',
        postId = '',
        uid = '',
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);
    const [competitionUIDs, setCompetitionUIDs] = useState(null);
    const [activeCompetitionUIDs, setActiveCompetitionUIDs] = useState(null);

    // Player Data
    const [storedData, setStoredData] = useState(null); // From WordPress
    const [data, setData] = useState(null); // From LIVE API
    const [status, setStatus] = useState(null);
    const [ competitionStats, setCompetitionStats ] = useState(null);
    const [ gameLog, setGameLog ] = useState(null);
    const [ gameLogCompetitions, setGameLogCompetitions ] = useState(null);
    const [ selectedGameLogCompetition, setSelectedGameLogCompetition ] = useState(null);
    const [ image, setImage ] = useState(null);
    const [clubData, setClubData] = useState(null);

    // Layout pieces
    const [ selectedTab, setSelectedTab ] = useState(1);


    /**
     * Get our connection info and stored data
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        let args = [
            { 
                key: 'player',
                id: postId
            },
            { 
                key: 'competitionUIDs'
            },
            { 
                key: 'activeUIDs'
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set player data
                if ( result.data.player ) {
                    let tempData = result.data.player;
                    let status = tempData.hasOwnProperty('status') ? tempData.status : 'TERMINATED';
                    setStatus(status);
                    setImage(tempData.hasOwnProperty('data') && tempData.data.hasOwnProperty('photo') ? tempData.data.photo : null);
                    setStoredData(tempData);
                }

                // Set all competition UIDs
                if ( result.data.competitionUIDs ) {
                    setCompetitionUIDs(result.data.competitionUIDs);
                }

                // Set active competition UIDs
                if ( result.data.activeUIDs ) {
                    setActiveCompetitionUIDs(result.data.activeUIDs);
                }
                
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);


    /**
     * Get Live API Data:
     * - Player Details
     */
    useEffect(() => {
        if ( !connectionInfo ) return;

        /** Get a player's details */
        const playerDetailsEndpoint = getEndpoint('player_details', {
            personId: uid,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);

        fetch(playerDetailsEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
        .then(res => res.json())
        .then(
        (result) => {
            if ( !result.hasOwnProperty('messages') ) {
                setData(result);
                if ( result.hasOwnProperty('club') ) {
                    setClubData(result.club);
                }
            }
            setIsLoaded(true);
        },
        (error) => {
            setIsLoaded(true);
            setError(error);
            console.log(error);
        }
        );


    }, [connectionInfo]);


    /**
     * Get Live API Data:
     * - Career Stats
     * - Game Log
     */
    useEffect(() => {
        if ( !connectionInfo || !competitionUIDs ) return;

        /** Get a player's competition stats */
        const competitionStatsEndpoint = getEndpoint('player_competition_stats', {
            personId: uid,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);

        fetch(competitionStatsEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
        .then(res => res.json())
        .then(
            (result) => {
                if ( !result.hasOwnProperty('messages') ) {
                    // Only get stats from competitions existing on the site
                    const validStats = result.filter((stat) => competitionUIDs.includes(stat.competition.id));
                    if ( validStats.length ) {
                        setCompetitionStats(validStats);
                    }
                }
            },
            (error) => {
                setIsLoaded(true);
                setError(error);
                console.log(error);
            }
        );


        /** Get a player's match appearances */
        const matchAppearancesEndpoint = getEndpoint('player_match_appearances', {
            personId: uid,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id,
            page: 1,
            pageSize: 9999
        }, connectionInfo.environment);

        fetch(matchAppearancesEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
        .then(res => res.json())
        .then(
            (result) => {
                if ( result.hasOwnProperty('result') ) {

                    // Only get games that have competitions existing on the site, and those that are already played
                    const validGames = result.result.filter((game) => competitionUIDs.includes(game.competition.id) && game.liveStatus == 'PLAYED');
                    setGameLog(validGames);

                    // Get the list of competitions
                    let competitions = [];
                    validGames.forEach((game) => {
                        if ( !game.hasOwnProperty('competition') || !game.competition ) return;
                        if ( competitions.findIndex(el => game.competition.id == el.id) == -1 ) {
                            competitions.push({
                                id: game.competition.id,
                                name: game.competition.name
                            });
                        }
                    });
                    if ( competitions.length ) {
                        setSelectedGameLogCompetition(competitions[0]);
                        setGameLogCompetitions(competitions);
                    }
                }
            },
            (error) => {
            setIsLoaded(true);
            setError(error);
            console.log(error);
            }
        );


    }, [connectionInfo, competitionUIDs]);


    /**
     * Handle tab changes
     */
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    /**
     * Handle game log competition change
     */
    const handleGameLogCompetitionChange = (event, value) => {
        const selectedGame = gameLogCompetitions.find(game => game.id == value);
        setSelectedGameLogCompetition(selectedGame);
    }


    // Bail if we don't have data yet
    if (!isLoaded ) {
        return displayLoader('acaLoader--main');
    }


    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>

            <Tabs className="acaPage acaPage--withSidebar acaPage--player" slots={{ root: 'section' }} value={ selectedTab }  onChange={handleTabChange}>

                <div className="acaMain acaMain--noPadding">

                    { /** Header */ }
                    <div className="acaHeader acaHeader--light acaHeader--player">
                        <div className="acaWrapper acaHeader__inner">
                            { image ? (
                                <div className="acaHeader__logo">
                                    <div className="teamLogo">
                                        <img src={image} className="teamLogo__inner" />
                                    </div>
                                </div>
                            ) : '' }
                            <div className="acaHeader__main">
                                { status !== 'TERMINATED' ? (
                                    <p className="acaH4 acaHeader__desc">
                                        { clubData && clubData.hasOwnProperty('name') ? clubData.name : 
                                            storedData && storedData.hasOwnProperty('clubName') ? storedData.clubName : '' }
                                        { data && data.hasOwnProperty('position') ? ' (' + data.position + ')' : '' }
                                    </p>
                                ) : '' }
                                <h1 className="acaH1 acaHeader__title">{name}</h1>
                            </div>
                            
                            <TabsList className="acaHeader__tabs acaLargeTabs" >
                                <Tab value={1}>Player Profile</Tab>
                                { gameLog && gameLog.length ? (
                                    <Tab value={2}>Game Log</Tab>
                                ) : '' }
                                { competitionStats ? (
                                    <Tab value={3}>Career Stats</Tab>
                                ) : '' }
                            </TabsList>
                        </div>
                    </div>

                    { /** Main Content */ }

                    { /** Player Profile */ }
                    <TabPanel value={1}>
                        
                        <div className="acaWrapper acaWrapper--withPadding paddingTop--small paddingBottom--small">

                            { /** 'No Data Available' message */ }
                            { !data || (!competitionStats && (!gameLog || !gameLog.length)) ? (
                                <div className="acaMessage acaMessage--centered">
                                    <h2 className="acaH4">No player data available</h2>
                                </div>
                            ) : '' }
                            
                            {/** Active competition stats */}
                            { competitionStats ? 
                                competitionStats
                                    .filter((stat) => activeCompetitionUIDs.includes(stat.competition.id))
                                    .map((stat, i) => (
                                        <div key={i} className="playerStatBox">
                                            <h2 className="acaH3 playerStatBox__title">{ stat.competition.name }</h2>
                                            <div className="playerStatBox__inner">
                                                <div className="playerStatBox__stat">
                                                    <span className="acaH1 playerStatBox__statNumber">{ stat.hasOwnProperty('matchesPlayed') ? stat.matchesPlayed : 0 }</span>
                                                    <span className="playerStatBox__statName">GP</span>
                                                </div>
                                                <div className="playerStatBox__stat">
                                                    <span className="acaH1 playerStatBox__statNumber">{ stat.hasOwnProperty('minutesPlayed') ? stat.minutesPlayed : 0 }</span>
                                                    <span className="playerStatBox__statName">MIN</span>
                                                </div>
                                                <div className="playerStatBox__stat">
                                                    <span className="acaH1 playerStatBox__statNumber">{ stat.hasOwnProperty('goals') ? stat.goals : 0 }</span>
                                                    <span className="playerStatBox__statName">G</span>
                                                </div>
                                                <div className="playerStatBox__stat">
                                                    <span className="acaH1 playerStatBox__statNumber">{ stat.hasOwnProperty('yellowCards') ? stat.yellowCards : 0 }</span>
                                                    <span className="playerStatBox__statName">YC</span>
                                                </div>
                                                <div className="playerStatBox__stat">
                                                    <span className="acaH1 playerStatBox__statNumber">{ stat.hasOwnProperty('redCards') ? stat.redCards : 0 }</span>
                                                    <span className="playerStatBox__statName">RC</span>
                                                </div>
                                                <div className="playerStatBox__stat">
                                                    <span className="acaH1 playerStatBox__statNumber">{ stat.hasOwnProperty('ownGoals') ? stat.ownGoals : 0 }</span>
                                                    <span className="playerStatBox__statName">OG</span>
                                                </div>
                                            </div>
                                        </div>
                            )) : '' }

                            { /** Recent Games */ }
                            { gameLog && gameLog.length ? (
                                <div className="paddingTop--small">

                                    <div className="acaBar acaBar--simple">
                                        <div className="acaBar__inner">
                                            <div className="acaBar__col acaBar__col--main">
                                                <h2 className="acaH2">Recent Games</h2>
                                            </div>
                                            <div className="acaBar__col">
                                                <button onClick={(e) => handleTabChange(e, 2)} className="acaBtn acaBtn--small acaBtn--black acaBtn--solid acaBtn--arrowRightIcon">See All Games</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="paddingTop--extraSmall gameLogs">
                                        { gameLog.slice(0, 5).map( (game, i) => (
                                            <GameLogBox key={i} data={game} />
                                        ) ) }
                                    </div>
                                </div>
                            ) : '' }

                        </div>

                    </TabPanel>

                    { /** Game Log */ }
                    <TabPanel value={2}>
                        <div className="acaWrapper acaWrapper--withPadding paddingTop--small paddingBottom--small">
                            { gameLog ? (
                                <>
                                    <div className="acaBar acaBar--simple">
                                        <div className="acaBar__inner">
                                            <div className="acaBar__col acaBar__col--main">
                                                <h2 className="acaH2">Games By Competition</h2>
                                            </div>
                                            <div className="acaBar__col">
                                            { /** Competitions Select */ }
                                            { gameLogCompetitions && gameLogCompetitions.length ? (
                                                <AcaSelect extraClasses="acaSelect--fullWidth" onChange={handleGameLogCompetitionChange} defaultValue={selectedGameLogCompetition ? selectedGameLogCompetition.id : ''} id="gamelog-competition" name="gamelog-competition">
                                                    { gameLogCompetitions.map((competition, i) => (
                                                        <Option key={i} value={competition.id}>{competition.name}</Option>
                                                    ))}
                                                </AcaSelect>
                                            ) : '' }
                                            </div>
                                        </div>
                                    </div>

                                    <div className="paddingTop--extraSmall gameLogs">
                                        { gameLog.filter(game => game.hasOwnProperty('competition') && game.competition && game.competition.id == selectedGameLogCompetition.id).map( (game, i) => (
                                            <GameLogBox key={i} data={game} />
                                        ) ) }
                                    </div>
                                </>
                            ) : '' }
                        </div>
                    </TabPanel>

                    { /** Career Stats */ }
                    <TabPanel value={3}>
                        <div className="acaWrapper acaWrapper--withPadding paddingTop--small paddingBottom--small">
                            { competitionStats ? competitionStats.map((stat, i) => (
                                <div key={i} className="careerStat">
                                    <h2 className="acaH3 careerStat__title">{ stat.competition.name }</h2>
                                    <div className="careerStat__inner">
                                        <div className="careerStat__stat">
                                            <span className="careerStat__statIcon careerStat__statIcon--matchesPlayed"></span>
                                            <span className="careerStat__statNumber">{ stat.hasOwnProperty('matchesPlayed') ? stat.matchesPlayed : 0 }</span>
                                        </div>
                                        <div className="careerStat__stat">
                                            <span className="careerStat__statIcon careerStat__statIcon--minutesPlayed"></span>
                                            <span className="careerStat__statNumber">{ stat.hasOwnProperty('minutesPlayed') ? stat.minutesPlayed : 0 }</span>
                                        </div>
                                        <div className="careerStat__stat">
                                            <span className="careerStat__statIcon careerStat__statIcon--goals"></span>
                                            <span className="careerStat__statNumber">{ stat.hasOwnProperty('goals') ? stat.goals : 0 }</span>
                                        </div>
                                        <div className="careerStat__stat">
                                            <span className="careerStat__statIcon careerStat__statIcon--yellowCards"></span>
                                            <span className="careerStat__statNumber">{ stat.hasOwnProperty('yellowCards') ? stat.yellowCards : 0 }</span>
                                        </div>
                                        <div className="careerStat__stat">
                                            <span className="careerStat__statIcon careerStat__statIcon--redCards"></span>
                                            <span className="careerStat__statNumber">{ stat.hasOwnProperty('redCards') ? stat.redCards : 0 }</span>
                                        </div>
                                    </div>
                                </div>
                            )) : '' }
                        </div>
                    </TabPanel>

                </div>

                <Sidebar>

                    <ErrorBoundary fallback={displayErrorMessage()}>
                        { status == 'TERMINATED' ? (
                            <div className="playerClubBox playerClubBox--terminated">
                                <TeamLogo defaultPictureId={null} fallback={getFallbackImage()} />
                                <h3 className="acaH2">No current club affiliation</h3>
                            </div>
                        ) : clubData ? (
                            <div className="playerClubBox">
                                <TeamLogo defaultPictureId={clubData.hasOwnProperty('picture') ? clubData.picture : null} fallback={getFallbackImage()} />
                                <h3 className="acaH2">{ clubData.hasOwnProperty('name') ? clubData.name : '' }</h3>
                            </div>
                        ) : storedData ? (
                            <div className="playerClubBox">
                                <TeamLogo teamId={storedData.hasOwnProperty('clubID') ? storedData.clubID : ''} fallback={getFallbackImage()} />
                                <h3 className="acaH2">{ storedData.hasOwnProperty('clubName') ? storedData.clubName : '' }</h3>
                            </div>
                        ) : '' }
                    </ErrorBoundary>

                </Sidebar>

            </Tabs>
        </ConnectionContext.Provider>
    );
}

export default PlayerApp;

// Render Match Page
const appContainer = document.getElementById('playerApp');
const root = createRoot(appContainer);
const name = appContainer.dataset.name;
const postId = appContainer.dataset.id;
const uid = appContainer.dataset.uid;
root.render(<PlayerApp postId={postId} uid={uid} name={name} />);